import React, { useState, useEffect, useCallback } from 'react';
import { Lock, LogOut, RefreshCw, Users, MailCheck, MessageSquareText, TrendingUp, Sparkles, Mail, MapPin, ChevronDown, ChevronUp } from 'lucide-react';
import SEO from '../components/SEO';

const TOKEN_KEY = 'gh_admin_token';

const useAdminApi = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || '');
  const apiBase = process.env.REACT_APP_BACKEND_URL;

  const call = useCallback(async (path) => {
    const res = await fetch(`${apiBase}${path}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (res.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      setToken('');
      throw new Error('Session expired. Please log in again.');
    }
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error(d.detail || `Request failed (${res.status})`);
    }
    return res.json();
  }, [apiBase, token]);

  const login = useCallback(async (password) => {
    const res = await fetch(`${apiBase}/api/admin/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error(d.detail || 'Login failed');
    }
    const { token: t } = await res.json();
    localStorage.setItem(TOKEN_KEY, t);
    setToken(t);
  }, [apiBase]);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken('');
  }, []);

  return { token, call, login, logout };
};

const LoginPanel = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try { await onLogin(password); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  return (
    <main className="min-h-screen bg-cream flex items-center justify-center px-4" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-md rounded-[1.75rem] bg-cream-2 border border-line p-8 md:p-10 shadow-[0_30px_60px_-30px_rgba(10,10,10,0.25)]">
        <div className="w-12 h-12 rounded-2xl bg-[var(--gh-orange)] text-white flex items-center justify-center">
          <Lock className="w-5 h-5" />
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-extrabold mt-6 text-ink leading-tight">
          Admin
        </h1>
        <p className="mt-2 text-sm text-muted-warm">
          Private dashboard for growthhackeng.com. Enter the admin password.
        </p>

        <label className="block mt-8">
          <span className="block text-xs uppercase tracking-widest font-semibold text-muted-warm mb-2">Password</span>
          <input
            type="password"
            required
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="•••••••••••••"
            data-testid="admin-password-input"
            className="w-full px-4 py-3 rounded-2xl bg-cream border border-line focus:border-[var(--gh-orange)] focus:ring-2 focus:ring-[var(--gh-orange)]/20 outline-none transition-all text-ink"
          />
        </label>

        {error && <p className="text-sm text-red-600 mt-4" data-testid="admin-login-error">{error}</p>}

        <button
          type="submit"
          disabled={busy || !password}
          data-testid="admin-login-submit"
          className="btn-orange w-full justify-center mt-6 disabled:opacity-60"
        >
          {busy ? 'Verifying…' : 'Sign in'}
        </button>
        <p className="text-[11px] text-muted-warm text-center mt-4">
          Sessions last 12 hours. Five failed attempts locks this IP for 15 minutes.
        </p>
      </form>
    </main>
  );
};

const StatCard = ({ icon: Icon, label, value, sub }) => (
  <div className="rounded-2xl bg-cream border border-line p-5">
    <div className="flex items-center justify-between">
      <span className="w-10 h-10 rounded-xl bg-[var(--gh-orange-soft)] text-[var(--gh-orange)] flex items-center justify-center">
        <Icon className="w-4 h-4" />
      </span>
    </div>
    <p className="mt-4 text-[10px] uppercase tracking-widest text-muted-warm font-semibold">{label}</p>
    <p className="font-display text-3xl font-extrabold text-ink mt-1 leading-none">{value}</p>
    {sub && <p className="mt-1 text-xs text-muted-warm">{sub}</p>}
  </div>
);

const shortDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(undefined, { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

const OverviewTab = ({ call }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(async () => {
    setLoading(true);
    try { setData(await call('/api/admin/summary')); setErr(''); }
    catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [call]);
  useEffect(() => { refresh(); }, [refresh]);

  if (loading) return <div className="text-muted-warm text-sm py-10">Loading overview…</div>;
  if (err) return <div className="text-red-600 text-sm py-10">{err}</div>;
  if (!data) return null;
  const c = data.counts || {};
  const cw = data.counts_last_7_days || {};
  return (
    <div data-testid="admin-overview">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Sparkles} label="Audit briefs" value={c.audit_submissions || 0} sub={`${cw.audit_submissions || 0} this week`} />
        <StatCard icon={Mail} label="Contact messages" value={c.contact_submissions || 0} sub={`${cw.contact_submissions || 0} this week`} />
        <StatCard icon={MailCheck} label="Newsletter subs" value={c.newsletter_subscriptions || 0} sub={`${cw.newsletter_subscriptions || 0} this week`} />
        <StatCard icon={MessageSquareText} label="Chat messages" value={c.chat_messages || 0} sub={`${cw.chat_messages || 0} this week`} />
      </div>

      {data.latest_audit && (
        <div className="mt-8 rounded-2xl bg-cream-2 border border-line p-6">
          <p className="text-[11px] uppercase tracking-widest text-[var(--gh-orange)] font-semibold">Latest audit</p>
          <div className="mt-3 flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <p className="font-display text-xl font-bold text-ink">{data.latest_audit.first_name}</p>
            <p className="text-sm text-muted-warm">{data.latest_audit.niche} · {data.latest_audit.list_size}</p>
            <p className="text-xs text-muted-warm ml-auto">{shortDate(data.latest_audit.created_at)}</p>
          </div>
          {data.latest_audit.ai_plan && (
            <div className="mt-4 rounded-xl bg-[var(--gh-orange-soft)] border border-[var(--gh-orange)] border-dashed p-4 text-sm text-ink whitespace-pre-wrap">
              {data.latest_audit.ai_plan}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AuditsTab = ({ call }) => {
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const d = await call('/api/admin/audits'); setItems(d.items || []); }
      catch (e) { setErr(e.message); }
      finally { setLoading(false); }
    })();
  }, [call]);

  if (loading) return <div className="text-muted-warm text-sm py-10">Loading audits…</div>;
  if (err) return <div className="text-red-600 text-sm py-10">{err}</div>;
  if (!items.length) return <div className="text-muted-warm text-sm py-10">No audit briefs yet.</div>;

  return (
    <div className="space-y-3" data-testid="admin-audits">
      {items.map((it) => (
        <div key={it.id} className="rounded-2xl bg-cream border border-line overflow-hidden">
          <button
            className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-cream-2 transition-colors"
            onClick={() => setExpanded(expanded === it.id ? null : it.id)}
            data-testid={`admin-audit-${it.id}`}
          >
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <p className="font-semibold text-ink">{it.first_name}</p>
                <p className="text-xs text-muted-warm">{it.niche} · {it.list_size} · {it.platform}</p>
              </div>
              <p className="text-xs text-muted-warm mt-1 truncate">{it.email} · <MapPin className="inline w-3 h-3" /> {it.location} · timeline: {it.timeline}</p>
            </div>
            <span className="text-xs text-muted-warm shrink-0">{shortDate(it.created_at)}</span>
            {expanded === it.id ? <ChevronUp className="w-4 h-4 text-muted-warm" /> : <ChevronDown className="w-4 h-4 text-muted-warm" />}
          </button>
          {expanded === it.id && (
            <div className="px-4 pb-5 space-y-3 text-sm border-t border-line pt-4 bg-cream-2">
              <FieldRow label="What's working" value={it.working} />
              <FieldRow label="What to fix" value={it.fix} />
              {it.ai_plan && (
                <div>
                  <p className="text-[10px] uppercase tracking-widest font-semibold text-[var(--gh-orange)]">AI draft · week-one plan</p>
                  <div className="mt-2 rounded-xl bg-[var(--gh-orange-soft)] border border-dashed border-[var(--gh-orange)] p-3 whitespace-pre-wrap text-ink">
                    {it.ai_plan}
                  </div>
                </div>
              )}
              <p className="text-[11px] text-muted-warm">
                Kit: {it.kit_subscriber_id || '—'} · Sheets: {it.sheets_delivered ? 'yes' : 'no'} · Resend: {(it.resend_id || '').slice(0, 10)}…
              </p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const FieldRow = ({ label, value }) => (
  <div>
    <p className="text-[10px] uppercase tracking-widest font-semibold text-muted-warm">{label}</p>
    <p className="mt-1 text-ink whitespace-pre-wrap">{value || '—'}</p>
  </div>
);

const ContactsTab = ({ call }) => {
  const [items, setItems] = useState([]);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const d = await call('/api/admin/contacts'); setItems(d.items || []); }
      catch (e) { setErr(e.message); }
      finally { setLoading(false); }
    })();
  }, [call]);
  if (loading) return <div className="text-muted-warm text-sm py-10">Loading contacts…</div>;
  if (err) return <div className="text-red-600 text-sm py-10">{err}</div>;
  if (!items.length) return <div className="text-muted-warm text-sm py-10">No contact messages yet.</div>;
  return (
    <div className="space-y-3" data-testid="admin-contacts">
      {items.map((it) => (
        <div key={it.id} className="rounded-2xl bg-cream border border-line p-4">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <p className="font-semibold text-ink">{it.first_name} {it.last_name || ''}</p>
            <p className="text-xs text-muted-warm">{it.email}</p>
            <p className="text-xs text-muted-warm ml-auto">{shortDate(it.created_at)} · {it.source}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

const NewsletterTab = ({ call }) => {
  const [items, setItems] = useState([]);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const d = await call('/api/admin/newsletter'); setItems(d.items || []); }
      catch (e) { setErr(e.message); }
      finally { setLoading(false); }
    })();
  }, [call]);
  if (loading) return <div className="text-muted-warm text-sm py-10">Loading subscribers…</div>;
  if (err) return <div className="text-red-600 text-sm py-10">{err}</div>;
  if (!items.length) return <div className="text-muted-warm text-sm py-10">No subscribers yet.</div>;
  return (
    <div className="rounded-2xl bg-cream border border-line overflow-hidden" data-testid="admin-newsletter">
      <table className="w-full text-sm">
        <thead className="bg-cream-2 text-muted-warm text-[10px] uppercase tracking-widest">
          <tr>
            <th className="px-4 py-3 text-left">Name</th>
            <th className="px-4 py-3 text-left">Email</th>
            <th className="px-4 py-3 text-left">Source</th>
            <th className="px-4 py-3 text-left">Kit ID</th>
            <th className="px-4 py-3 text-left">When</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.id} className="border-t border-line hover:bg-cream-2">
              <td className="px-4 py-3 text-ink">{it.name || '—'}</td>
              <td className="px-4 py-3 text-ink">{it.email}</td>
              <td className="px-4 py-3 text-muted-warm">{it.source}</td>
              <td className="px-4 py-3 text-muted-warm text-xs">{it.kit_subscriber_id || '—'}</td>
              <td className="px-4 py-3 text-muted-warm text-xs">{shortDate(it.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ChatsTab = ({ call }) => {
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const d = await call('/api/admin/chats'); setItems(d.items || []); }
      catch (e) { setErr(e.message); }
      finally { setLoading(false); }
    })();
  }, [call]);
  if (loading) return <div className="text-muted-warm text-sm py-10">Loading chats…</div>;
  if (err) return <div className="text-red-600 text-sm py-10">{err}</div>;
  if (!items.length) return <div className="text-muted-warm text-sm py-10">No chat conversations yet.</div>;
  return (
    <div className="space-y-3" data-testid="admin-chats">
      {items.map((s) => (
        <div key={s.session_id} className="rounded-2xl bg-cream border border-line overflow-hidden">
          <button
            className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-cream-2 transition-colors"
            onClick={() => setExpanded(expanded === s.session_id ? null : s.session_id)}
          >
            <div className="flex-1 min-w-0">
              <p className="font-mono text-xs text-muted-warm truncate">{s.session_id}</p>
              <p className="text-xs text-muted-warm mt-1">{s.message_count} messages · last at {shortDate(s.last_at)}</p>
            </div>
            {expanded === s.session_id ? <ChevronUp className="w-4 h-4 text-muted-warm" /> : <ChevronDown className="w-4 h-4 text-muted-warm" />}
          </button>
          {expanded === s.session_id && (
            <div className="border-t border-line bg-cream-2 p-4 space-y-2 max-h-96 overflow-y-auto">
              {s.messages.map((m, i) => (
                <div key={m.id || m.created_at || i} className={`text-sm ${m.role === 'user' ? 'text-ink' : 'text-muted-warm'}`}>
                  <span className="text-[10px] uppercase tracking-widest font-semibold mr-2">{m.role === 'user' ? 'Visitor' : 'AI'}</span>
                  <span className="whitespace-pre-wrap">{m.content}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const TABS = [
  { id: 'overview', label: 'Overview', icon: TrendingUp },
  { id: 'audits', label: 'Audit briefs', icon: Sparkles },
  { id: 'contacts', label: 'Contacts', icon: Mail },
  { id: 'newsletter', label: 'Newsletter', icon: MailCheck },
  { id: 'chats', label: 'Chats', icon: MessageSquareText },
];

const Admin = () => {
  const { token, call, login, logout } = useAdminApi();
  const [tab, setTab] = useState('overview');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => { document.body.style.background = 'var(--gh-cream)'; }, []);

  if (!token) return (
    <>
      <SEO title="Admin · Growth Hackeng" description="" canonical="/admin" noindex />
      <LoginPanel onLogin={login} />
    </>
  );

  const bump = () => setRefreshKey((k) => k + 1);

  return (
    <>
      <SEO title="Admin · Growth Hackeng" description="" canonical="/admin" noindex />
      <main className="min-h-screen bg-cream" data-testid="admin-dashboard">
        <div className="border-b border-line bg-cream-2/70 backdrop-blur-sm sticky top-0 z-10">
          <div className="container-x flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-xl bg-[var(--gh-orange)] text-white flex items-center justify-center">
                <Users className="w-4 h-4" />
              </span>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-muted-warm font-semibold">Growth Hackeng</p>
                <p className="font-display text-lg font-bold text-ink leading-none">Admin</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={bump} data-testid="admin-refresh" className="w-9 h-9 rounded-full border border-line hover:border-ink text-ink flex items-center justify-center transition-colors" aria-label="Refresh">
                <RefreshCw className="w-4 h-4" />
              </button>
              <button onClick={logout} data-testid="admin-logout" className="btn-outline text-sm py-2 px-4">
                <LogOut className="w-4 h-4" /> Log out
              </button>
            </div>
          </div>
          <div className="container-x flex gap-1 overflow-x-auto pb-3">
            {TABS.map((t) => {
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`admin-tab-${t.id}`}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
                    active ? 'bg-ink text-[var(--gh-cream)]' : 'text-muted-warm hover:text-ink hover:bg-cream-2'
                  }`}
                >
                  <t.icon className="w-3.5 h-3.5" />
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="container-x py-8" key={`${tab}-${refreshKey}`}>
          {tab === 'overview' && <OverviewTab call={call} />}
          {tab === 'audits' && <AuditsTab call={call} />}
          {tab === 'contacts' && <ContactsTab call={call} />}
          {tab === 'newsletter' && <NewsletterTab call={call} />}
          {tab === 'chats' && <ChatsTab call={call} />}
        </div>
      </main>
    </>
  );
};

export default Admin;
